/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useState, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { getMinuteDifference } from '@utils/datetime';
import LockConfirmationDialog from './LockConfirmationDialog';
import { useLockTestletMutation, useUnlockTestletMutation } from '../../mutations';
import UnlockConfirmationDialog from './UnlockConfirmationDialog';
import UpdateConfirmationDialog from './UpdateConfirmationDialog';
import { LockStrategy, Testlet } from '../../types';

type ConfirmationDialogContextType = {
  showLockConfirmationDialog: (testlet: Testlet) => void;
  showUnlockConfirmationDialog: (testlet: Testlet) => void;
  showUpdateConfirmationDialog: (testlet: Testlet) => void;
};

const ConfirmationDialogContext = createContext<ConfirmationDialogContextType>({
  showLockConfirmationDialog: () => {},
  showUnlockConfirmationDialog: () => {},
  showUpdateConfirmationDialog: () => {},
});

export const ConfirmationDialogProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { testSessionId } = useParams();
  const { mutate: lockTestlet } = useLockTestletMutation(testSessionId);
  const { mutate: unlockTestlet } = useUnlockTestletMutation(testSessionId);
  const [openLockConfirmationDialog, setOpenLockConfirmationDialog] = useState(false);
  const [openUnlockConfirmationDialog, setOpenUnlockConfirmationDialog] = useState(false);
  const [openUpdateConfirmationDialog, setOpenUpdateConfirmationDialog] = useState(false);
  const [testlet, setTestlet] = useState<Testlet | undefined>();
  const [autoLockDuration, setAutoLockDuration] = useState<number>(0);

  const showLockConfirmationDialog = React.useCallback(
    (selectedTestlet: Testlet) => {
      setOpenLockConfirmationDialog(true);
      setTestlet(selectedTestlet);
    },
    [setOpenLockConfirmationDialog, setTestlet],
  );

  const showUnlockConfirmationDialog = React.useCallback(
    (selectedTestlet: Testlet) => {
      setOpenUnlockConfirmationDialog(true);
      setTestlet(selectedTestlet);
    },
    [setOpenUnlockConfirmationDialog, setTestlet],
  );

  const showUpdateConfirmationDialog = React.useCallback(
    (selectedTestlet: Testlet) => {
      setOpenUpdateConfirmationDialog(true);
      setTestlet(selectedTestlet);
    },
    [setOpenUpdateConfirmationDialog, setTestlet],
  );

  const value = useMemo(
    () => ({
      showLockConfirmationDialog,
      showUnlockConfirmationDialog,
      showUpdateConfirmationDialog,
    }),
    [
      showLockConfirmationDialog,
      showUnlockConfirmationDialog,
      showUpdateConfirmationDialog,
    ],
  );

  return (
    <ConfirmationDialogContext.Provider value={value}>
      {children}
      <LockConfirmationDialog
        key={openLockConfirmationDialog ? 'open' : 'closed'}
        open={openLockConfirmationDialog}
        testletLockStrategy={testlet?.lock_strategy}
        onCancel={() => setOpenLockConfirmationDialog(false)}
        onConfirm={(lockStrategy: LockStrategy) => {
          if (testlet) {
            lockTestlet({ testletId: testlet.id,  lockStrategy});
          }
          setOpenLockConfirmationDialog(false);
        }}
      />
      <UnlockConfirmationDialog
        key={openUnlockConfirmationDialog ? 'unlock-open' : 'unlock-closed'}
        open={openUnlockConfirmationDialog}
        onCancel={() => setOpenUnlockConfirmationDialog(false)}
        onConfirm={(lockStrategy: LockStrategy) => {
          if (testlet) {
            unlockTestlet({
              testletId: testlet.id,
              body: {
                auto_lock_duration: autoLockDuration,
                lock_strategy: lockStrategy,
              },
            });
          }
          setOpenUnlockConfirmationDialog(false);
        }}
        setAutoLockDuration={setAutoLockDuration}
      />
      <UpdateConfirmationDialog
        key={openUpdateConfirmationDialog ? 'update-open' : 'update-closed'}
        open={openUpdateConfirmationDialog}
        onCancel={() => setOpenUpdateConfirmationDialog(false)}
        onConfirm={(autoLockDate, lockStrategy: LockStrategy) => {
          if (testlet && testlet.unlocked_at && autoLockDate) {
            unlockTestlet({
              testletId: testlet.id,
              body: {
                auto_lock_duration: getMinuteDifference(testlet.unlocked_at, autoLockDate as Date) ?? 0,
                lock_strategy: lockStrategy,
                update: true,
              },
            });
            setOpenUpdateConfirmationDialog(false);
          }
        }}
        testlet={testlet as Testlet}
      />
    </ConfirmationDialogContext.Provider>
  );
};

export default ConfirmationDialogProvider;

export const useExamActionConfirmationDialog = () => {
  const context = useContext(ConfirmationDialogContext);
  if (!context) {
    throw new Error(
      'useExamActionConfirmationDialog must be used within a ConfirmationDialogProvider',
    );
  }
  return context;
};
